import { ref, Ref, computed, nextTick } from 'vue';
import { INoteTranscript, IncomingTranscript, TranscriptionState } from '@/types/transcript';
import { nanoid } from 'nanoid';
import constants from '@/utilities/constants';
import { useStore } from 'vuex';
import { NoteTranscriptSource } from '@superthread-com/api';
import { browserStorageSetItem } from '@/utilities/browser-storage.util';
import { noteTranscriptPersistKey, AutoTranscriptionMode } from '@/types/notes';
import useTranslate from '@/utilities/composables/useTranslate';
import useIsElectronApp from '@/utilities/composables/useIsElectronApp';

export default function useAudioTranscription(noteId: Ref<string> = ref('newnote')) {
  const store = useStore();
  const { translate } = useTranslate();

  //@ts-ignore
  const electronAPI = window.electron;

  const { isElectronApp } = useIsElectronApp();

  const transcriptionState = computed((): TranscriptionState => {
    return store.getters.getTranscriptionState;
  });

  // recording control functions
  const startRecording = async (persistedNoteId: string = '', attachCurrentDate?: boolean) => {
    if (!isRecordingStopped.value) return;

    // note audio transcription is only available for teams that have `nat` feature flag enabled (paid plan and custom plans)
    if (!hasNoteTranscriptionFeatureFlag.value) return;

    // if we start recording on new note, create it before transcribing
    const newNoteId =
      persistedNoteId ||
      (noteId.value === constants.newNote ? await createNote(attachCurrentDate) : noteId.value);

    const transcriptionKeywords = store.getters.getUser.transcription_keywords || [];
    const memberKeywords = Object.values(store.getters.getTeamMembers || {}).map(
      (m: any) => m.first_name || m.display_name
    );
    electronAPI?.send('deepgram-keywords-channel', [...transcriptionKeywords, ...memberKeywords]);
    electronAPI?.send(
      'deepgram-language-channel',
      store.getters.getUserSettings.transcription_language || 'en'
    );

    nextTick(() => {
      electronAPI?.send('recording-channel', 'start');
      store.dispatch('setTranscriptionState', {
        recording: 'pending',
        noteId: newNoteId,
        alreadyRecorded: transcriptionState.value.alreadyRecorded.add(newNoteId),
      });
    });

    setupChannels();
  };

  const alreadyRecordedForCurrentNote = computed(() => {
    return transcriptionState.value.alreadyRecorded.has(noteId.value);
  });

  const saveUnsavedTranscripts = async (noteId: string) => {
    const unsavedTranscripts =
      store.getters.getNotes[noteId]?.transcripts?.filter((t: INoteTranscript) => t.ongoing) || [];

    if (unsavedTranscripts.length > 0) {
      store
        .dispatch('createTranscript', {
          noteId,
          transcripts: unsavedTranscripts.map((t: INoteTranscript) => ({
            source: t.source,
            content: !t.ongoing ? t.content : `${t.content || ''} ${t.interim_content}`,
          })),
        })
        .then(({ data }: any) => {
          store.dispatch('localSetNoteTranscripts', {
            noteId,
            transcripts: data.note.transcripts,
          });
        });
    }
  };

  const startRecordingForAnotherNote = async (newNoteForRecording: string) => {
    // note audio transcription is only available for teams that have `nat` feature flag enabled (paid plan and custom plans)
    if (!hasNoteTranscriptionFeatureFlag.value) return;

    saveUnsavedTranscripts(currentTranscriptionNoteId.value);
    const newNoteId =
      newNoteForRecording === constants.newNote ? await createNote() : newNoteForRecording;

    store.dispatch('setTranscriptionState', {
      noteId: newNoteId,
      alreadyRecorded: transcriptionState.value.alreadyRecorded.add(newNoteId),
    });
  };

  const stopRecording = (persistNoteRecording = false) => {
    if (persistNoteRecording && transcriptionState.value.recording === 'started') {
      browserStorageSetItem(noteTranscriptPersistKey, currentTranscriptionNoteId.value);
    }

    saveUnsavedTranscripts(currentTranscriptionNoteId.value);
    electronAPI?.send('recording-channel', 'stop');
    store.dispatch('setTranscriptionState', { recording: 'stopped', noteId: '' });
    removeChannels();
  };

  const currentTranscriptionNoteId = computed(() => {
    return transcriptionState.value.noteId;
  });

  const currentTranscriptionNoteTitle = computed(() => {
    return (
      store.getters.getNotes[currentTranscriptionNoteId.value]?.title || translate('untitledNote')
    );
  });

  const isRecordingPending = computed((): boolean => {
    return !!currentTranscriptionNoteId.value && transcriptionState.value.recording === 'pending';
  });

  const isRecordingStarted = computed((): boolean => {
    return !!currentTranscriptionNoteId.value && transcriptionState.value.recording === 'started';
  });

  const isRecordingStopped = computed((): boolean => {
    return transcriptionState.value.recording === 'stopped';
  });

  // create a new note
  const createNote = async (attachCurrentDate?: boolean) => {
    return store.dispatch('createNewNote', {
      title: '',
      attachCurrentDate,
    });
  };

  const transcripts = computed((): INoteTranscript[] => {
    return store.getters.getNotes[noteId.value]?.transcripts || [];
  });

  const hasTranscripts = computed((): boolean => {
    return Boolean(transcripts.value.length);
  });

  const isTranscriptDone = (t: IncomingTranscript) => {
    return t.is_final && /^[^]*[.!?]\s*$/.test((t.content ?? '').trim());
  };

  const createInterimTranscript = (t: IncomingTranscript) => {
    store.dispatch('localCreateTranscript', {
      noteId: currentTranscriptionNoteId.value,
      transcript: {
        local_id: nanoid(6),
        source: t.source,
        ongoing: true,
        content: t.is_final ? t.content : '',
        interim_content: t.is_final ? '' : t.content,
      },
    });
  };

  const createFinalTranscript = (t: INoteTranscript) => {
    store.dispatch('createTranscript', {
      noteId: currentTranscriptionNoteId.value,
      transcripts: [
        {
          source: t.source,
          content: t.content || '',
        },
      ],
    });
  };

  const processTranscript = async (t: IncomingTranscript) => {
    if (!isRecordingStarted.value) return;

    const notes = store.getters.getNotes;
    const note = notes[currentTranscriptionNoteId.value];
    if (!note) return;

    const { transcripts = [] } = note;

    // find last transcription of the same source
    const lastTranscriptIndex = transcripts
      .slice()
      .reverse()
      .findIndex((ti: any) => ti.source === t.source);

    const lastTranscript = transcripts[transcripts.length - lastTranscriptIndex - 1];

    // transcript is considered done if its content is final version of interim result and if it is a finished sentence
    const newTranscriptIsDone = isTranscriptDone(t);

    // handle utterance end event to finalize the last transcripti if its still ongoing (grayed out in UI)
    if (t.utterance_end && lastTranscript.ongoing) {
      lastTranscript.content = `${lastTranscript.content ?? ''} ${lastTranscript.interim_content ?? ''}`;
      store.dispatch('localUpdateTranscript', {
        noteId: currentTranscriptionNoteId.value,
        transcript: {
          local_id: lastTranscript.local_id,
          content: lastTranscript.content,
          interim_content: '',
          ongoing: false,
        },
      });
      createFinalTranscript({
        source: t.source,
        content: lastTranscript.content,
      });
      return;
    }

    if (!t.content) return;

    // if this is the first transcript of the source or if last transcript is done, create a new one
    if (lastTranscriptIndex == -1 || !lastTranscript.ongoing) {
      if (newTranscriptIsDone) {
        createFinalTranscript(t);
      } else {
        createInterimTranscript(t);
      }

      return;
    }

    // if this is a final version of the last transcript, update the content
    if (t.is_final) {
      lastTranscript.content = `${lastTranscript.content} ${t.content}`;
      lastTranscript.interim_content = '';
    } else {
      // if interim result is not done generating, update interim content
      lastTranscript.interim_content = t.content;
    }

    lastTranscript.ongoing = !newTranscriptIsDone;

    // update the transcript locally
    store.dispatch('localUpdateTranscript', {
      noteId: currentTranscriptionNoteId.value,
      transcript: {
        local_id: lastTranscript.local_id,
        content: lastTranscript.content,
        interim_content: lastTranscript.interim_content,
        ongoing: !newTranscriptIsDone,
      },
    });

    // if transcript is done, save it on the BE
    if (newTranscriptIsDone) {
      createFinalTranscript({ source: lastTranscript.source, content: lastTranscript.content });
    }
  };

  // sets up the transcription channel to listen to incoming transcriptions
  const setupChannels = () => {
    electronAPI.on('transcription-channel', (_: any, t: IncomingTranscript) => {
      processTranscript(t);
    });

    electronAPI.on('recording-channel', (_: any, recording: string) => {
      store.dispatch('setTranscriptionState', {
        ...(recording === 'stopped' && { noteId: '' }),
        recording,
      });

      // TOOD(senad): add a logic to stop recording when recieving 'stopped' event
    });
  };

  const removeChannels = () => {
    electronAPI.removeAllListeners('transcription-channel');
    electronAPI.removeAllListeners('recording-channel');
  };

  // setups up a listener to receive audio permissions
  const audioPermissionsCheck = () => {
    electronAPI.on('permissions-channel', (_: any, perm: string) => {
      const { source, permission_status } = JSON.parse(perm);
      //@ts-ignore
      store.dispatch('setTranscriptionState', {
        permissions: {
          ...transcriptionState.value.permissions,
          [source]: permission_status,
        },
      });
    });

    electronAPI.send('permissions-channel', 'get-microphone');
    electronAPI.send('permissions-channel', 'get-system');
  };

  const hasSystemAudioPermission = computed(() => {
    return transcriptionState.value.permissions.system === 'authorized';
  });

  const hasMicrophonePermission = computed(() => {
    return transcriptionState.value.permissions.microphone === 'authorized';
  });

  const hasAllPermissions = computed(() => {
    return isElectronApp.value
      ? hasSystemAudioPermission.value && hasMicrophonePermission.value
      : true;
  });

  const isMicrophonePending = computed(() => {
    return transcriptionState.value.permissions.microphone === 'pending';
  });

  const isSystemAudioPending = computed(() => {
    return transcriptionState.value.permissions.system === 'pending';
  });

  const isMicrophoneAuthorized = computed(() => {
    return transcriptionState.value.permissions.microphone === 'authorized';
  });

  const isSystemAudioAuthorized = computed(() => {
    return transcriptionState.value.permissions.system === 'authorized';
  });

  const requestPermission = (source: 'microphone' | 'system', callback = () => {}) => {
    const permissionStatus = transcriptionState.value.permissions[source];

    switch (permissionStatus) {
      case 'authorized':
        break;
      case 'denied':
        callback();
        break;
      default:
        electronAPI.send('permissions-channel', `request-${source}`);
        break;
    }
  };

  const getPermission = (source: 'microphone' | 'system') => {
    electronAPI.send('permissions-channel', `get-${source}`);
  };

  const getTranscriptContents = (separator: string = '\n', noFormatting = true) => {
    if (noFormatting)
      return transcripts.value.map((t: INoteTranscript) => t.content).join(separator);

    return transcripts.value.reduce((acc: string, t: INoteTranscript, index: number) => {
      const sourceFrom = t.source === NoteTranscriptSource.Author ? 'Me' : 'Them';

      if (index === 0) return `${sourceFrom}: ${t.content}`;

      const prevTranscript = transcripts.value[index - 1];
      return acc + (prevTranscript.source !== t.source ? `\n${sourceFrom}: ` : ' ') + t.content;
    }, '');
  };

  const hasNoteTranscriptionFeatureFlag = computed(() => {
    return store.getters.getTeamFeatureEnabled(constants.featureFlagIds.noteAudioTranscription);
  });

  const isAutoStartTranscriptionEnabled = computed(() => {
    return store.getters.getUserSettings.auto_start_transcription;
  });

  const isAutoStartTranscriptionMode = computed(() => {
    return store.getters.getUserSettings.auto_start_transcription_mode;
  });

  const isAutoStartTranscriptionModeAlways = computed(() => {
    return (
      isAutoStartTranscriptionMode.value === AutoTranscriptionMode.Always &&
      isAutoStartTranscriptionEnabled.value
    );
  });

  const isAutoStartTranscriptionModeMeetingOnly = computed(() => {
    return (
      isAutoStartTranscriptionMode.value === AutoTranscriptionMode.MeetingOnly &&
      isAutoStartTranscriptionEnabled.value
    );
  });

  return {
    startRecording,
    startRecordingForAnotherNote,
    stopRecording,
    isRecordingStopped,
    isRecordingPending,
    isRecordingStarted,
    alreadyRecordedForCurrentNote,

    setupChannels,
    removeChannels,

    transcriptionState,
    transcripts,
    hasTranscripts,
    processTranscript,
    isTranscriptDone,

    audioPermissionsCheck,
    hasMicrophonePermission,
    hasSystemAudioPermission,
    hasAllPermissions,
    requestPermission,
    getPermission,

    isMicrophonePending,
    isSystemAudioPending,
    isMicrophoneAuthorized,
    isSystemAudioAuthorized,

    createFinalTranscript,
    createInterimTranscript,
    createNote,

    getTranscriptContents,
    currentTranscriptionNoteId,
    currentTranscriptionNoteTitle,

    hasNoteTranscriptionFeatureFlag,

    isAutoStartTranscriptionEnabled,
    isAutoStartTranscriptionMode,
    isAutoStartTranscriptionModeAlways,
    isAutoStartTranscriptionModeMeetingOnly,
  };
}
